import { motion } from "framer-motion";
import { useRef, type ReactNode } from "react";

import { NAV_PANEL_MOTION } from "@/blueprint/tokens/Motion";

const ENTER_EASE = [...NAV_PANEL_MOTION.openEase] as [
  number,
  number,
  number,
  number,
];

type NavPanelRowProps = {
  /** Position down the panel — 0 is the topmost row. Drives the stagger. */
  index: number;
  open: boolean;
  reducedMotion: boolean;
  /** Whether the panel was shut when this row mounted. See the latch below. */
  panelWasClosed: boolean;
  className?: string | undefined;
  children: ReactNode;
};

/**
 * One staggered row of the hover panel: rows rise and fade in behind the box,
 * top to bottom. They leave together rather than in sequence — a dismissal the
 * user has already committed to should not be spelled out one row at a time —
 * and they clear out ahead of the box so the panel is empty before it shuts.
 */
const NavPanelRow = ({
  index,
  open,
  reducedMotion,
  panelWasClosed,
  className,
  children,
}: NavPanelRowProps) => {
  // Latched at mount. Rows that appear during a tab swap inside an already-open
  // panel belong to the crossfade, which moves the incoming block as one piece;
  // only rows arriving with the panel itself stagger. Reading `open` live here
  // instead would restage every row on each swap.
  const staggerRef = useRef(panelWasClosed);

  if (!staggerRef.current) return <div className={className}>{children}</div>;

  return (
    <motion.div
      // A real initial, not `false`: the row mounts in the same commit that
      // opens the panel, so `false` would snap it visible and skip the stagger.
      initial={{ opacity: 0, y: NAV_PANEL_MOTION.contentRisePx }}
      animate={{
        opacity: open ? 1 : 0,
        y: open ? 0 : NAV_PANEL_MOTION.contentRisePx,
      }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : open
            ? {
                duration: NAV_PANEL_MOTION.contentEnterMs / 1000,
                ease: ENTER_EASE,
                delay:
                  (NAV_PANEL_MOTION.contentDelayMs +
                    index * NAV_PANEL_MOTION.contentStaggerMs) /
                  1000,
              }
            : {
                duration: NAV_PANEL_MOTION.contentExitMs / 1000,
                ease: ENTER_EASE,
              }
      }
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default NavPanelRow;
